import React, { useState, useEffect } from 'react';
import { LabelSchema, LabelClass, FeaturedObject } from '../types/labeling';
import { useLabelAttributes } from '../hooks/useLabelAttributes';
import { LabelAttribute } from '../types/labelAttribute';

interface SchemaFormProps {
  schema?: LabelSchema;
  categories?: any[];
  parentCategories?: any[];
  globalObjects?: any[];
  existingSchemas?: LabelSchema[];
  onSave: (schema: Partial<LabelSchema>) => void;
  onCancel: () => void;
}

export default function SchemaForm({ schema, categories = [], parentCategories = [], globalObjects = [], existingSchemas = [], onSave, onCancel }: SchemaFormProps) {
  const [name, setName] = useState(schema?.name || '');
  const [scope, setScope] = useState<'detection'>(schema?.scope || 'detection');
  const [guidelines, setGuidelines] = useState(schema?.guidelines || '');
  const [classes, setClasses] = useState<LabelClass[]>(schema?.classes || []);
  const [featuredObjects, setFeaturedObjects] = useState<FeaturedObject[]>(schema?.featuredObjects || []);
  const [includeGlobalCategories, setIncludeGlobalCategories] = useState(schema?.includeGlobalCategories || false);
  const [selectedGlobalCategories, setSelectedGlobalCategories] = useState<string[]>(schema?.selectedGlobalCategories || []);
  const [imageAttributes, setImageAttributes] = useState<string[]>(schema?.imageAttributes || []);
  const [globalAttributes, setGlobalAttributes] = useState<string[]>(schema?.globalAttributes || []);
  const [createNewVersion, setCreateNewVersion] = useState(false);
  const [expandedParentCategories, setExpandedParentCategories] = useState<Set<string>>(new Set());
  const [expandedClassAttributeSections, setExpandedClassAttributeSections] = useState<Set<string>>(new Set());

  const { attributes: labelAttributes, loading: attributesLoading } = useLabelAttributes();

  const activeImageAttributes = labelAttributes.filter(
    (attr) => attr.level === 'image' && attr.active !== false
  );
  const activeInstanceAttributes = labelAttributes.filter(
    (attr) => attr.level === 'instance' && attr.active !== false
  );
  
  // Object selection state (for Label Classes "Add Existing Objects")
  const [showObjectSelector, setShowObjectSelector] = useState(false);
  const [objectSearchTerm, setObjectSearchTerm] = useState('');
  const [selectedObjects, setSelectedObjects] = useState<string[]>([]);

  // Featured objects picker state
  const [showFeaturedSelector, setShowFeaturedSelector] = useState(false);
  const [featuredSearchTerm, setFeaturedSearchTerm] = useState('');
  const [selectedFeaturedIds, setSelectedFeaturedIds] = useState<string[]>([]);

  const addClass = () => {
    const newClass: LabelClass = {
      id: `class_${Date.now()}`,
      name: '',
      displayName: '',
      description: '',
      hotkey: '',
      shapes: ['rectangle'], // default to rectangle for detection
      category: '', // parent category (optional)
      subcategory: '', // subcategory (required)
    };
    setClasses([...classes, newClass]);
  };

  const updateClass = (
    index: number,
    field: keyof LabelClass,
    value: LabelClass[keyof LabelClass]
  ) => {
    const updatedClasses = [...classes];
    updatedClasses[index] = { ...updatedClasses[index], [field]: value };
    setClasses(updatedClasses);
  };

  const removeClass = (index: number) => {
    setClasses(classes.filter((_, i) => i !== index));
  };

  // Object selection functions
  // Combine all objects from global and schemas, deduplicating by ID
  const allObjects = [
    ...globalObjects,
    ...existingSchemas.flatMap(schema => schema.classes || [])
  ];
  
  // Deduplicate by ID - keep first occurrence
  const seenIds = new Set<string>();
  const availableObjects = allObjects.filter(obj => {
    const id = obj.id || obj.name;
    if (!id || seenIds.has(id)) {
      return false;
    }
    seenIds.add(id);
    return true;
  });

  const filteredObjects = availableObjects.filter(obj => 
    obj.name?.toLowerCase().includes(objectSearchTerm.toLowerCase()) ||
    obj.displayName?.toLowerCase().includes(objectSearchTerm.toLowerCase())
  );

  const addSelectedObjects = () => {
    const objectsToAdd = availableObjects.filter(obj => selectedObjects.includes(obj.id));
    const newClasses = objectsToAdd.map(obj => ({
      id: `class_${Date.now()}_${Math.random()}`,
      name: obj.name,
      displayName: obj.displayName,
      description: obj.description || '',
      hotkey: obj.hotkey || '',
      shapes: obj.shapes || ['rectangle'],
      category: obj.category || '', // NEW: top-level category (optional)
      subcategory: obj.subcategory || obj.category || '', // RENAMED: subcategory (fallback for migration)
      attributes: obj.attributes || [],
    }));
    
    setClasses([...classes, ...newClasses]);
    setSelectedObjects([]);
    setShowObjectSelector(false);
    setObjectSearchTerm('');
  };

  const toggleObjectSelection = (objectId: string) => {
    setSelectedObjects(prev => 
      prev.includes(objectId) 
        ? prev.filter(id => id !== objectId)
        : [...prev, objectId]
    );
  };

  const featuredObjectIdSet = new Set(featuredObjects.map(f => f.objectId));

  const featureableGlobals = globalObjects.filter((obj: any) => {
    const groupKey = obj.subcategory || obj.category;
    if (!groupKey || !selectedGlobalCategories.includes(groupKey)) return false;
    if (featuredObjectIdSet.has(obj.id)) return false;
    return true;
  });

  const filteredFeatureableGlobals = featureableGlobals.filter((obj: any) =>
    obj.name?.toLowerCase().includes(featuredSearchTerm.toLowerCase()) ||
    obj.displayName?.toLowerCase().includes(featuredSearchTerm.toLowerCase())
  );

  const toggleFeaturedSelection = (objectId: string) => {
    setSelectedFeaturedIds(prev =>
      prev.includes(objectId)
        ? prev.filter(id => id !== objectId)
        : [...prev, objectId]
    );
  };

  const addSelectedFeaturedObjects = () => {
    const toAdd = featureableGlobals
      .filter((obj: any) => selectedFeaturedIds.includes(obj.id))
      .map((obj: any) => ({ objectId: obj.id as string, hotkey: '' }));
    setFeaturedObjects([...featuredObjects, ...toAdd]);
    setSelectedFeaturedIds([]);
    setShowFeaturedSelector(false);
    setFeaturedSearchTerm('');
  };

  const removeFeaturedObject = (objectId: string) => {
    setFeaturedObjects(featuredObjects.filter(f => f.objectId !== objectId));
  };

  const updateFeaturedHotkey = (objectId: string, hotkey: string) => {
    const next = hotkey.slice(0, 1).toLowerCase();
    setFeaturedObjects(
      featuredObjects.map(f =>
        f.objectId === objectId ? { ...f, hotkey: next } : f
      )
    );
  };

  const resolveFeaturedObject = (objectId: string) =>
    globalObjects.find((obj: any) => obj.id === objectId);

  // Helper functions for parent category selection
  const getSubcategoriesForParent = (parentId: string) => {
    const filtered = categories.filter(cat => cat.parentCategoryId === parentId);
    // Deduplicate by ID to prevent duplicate keys
    const seen = new Set<string>();
    return filtered.filter(cat => {
      if (seen.has(cat.id)) {
        return false;
      }
      seen.add(cat.id);
      return true;
    });
  };

  const areAllSubcategoriesSelected = (parentId: string) => {
    const subcategories = getSubcategoriesForParent(parentId);
    if (subcategories.length === 0) return false;
    return subcategories.every(sub => selectedGlobalCategories.includes(sub.id));
  };

  const areSomeSubcategoriesSelected = (parentId: string) => {
    const subcategories = getSubcategoriesForParent(parentId);
    return subcategories.some(sub => selectedGlobalCategories.includes(sub.id)) && 
           !areAllSubcategoriesSelected(parentId);
  };

  const toggleParentCategory = (parentId: string) => {
    const subcategories = getSubcategoriesForParent(parentId);
    const subcategoryIds = subcategories.map(sub => sub.id);
    
    if (areAllSubcategoriesSelected(parentId)) {
      // Unselect all subcategories
      setSelectedGlobalCategories(prev => 
        prev.filter(id => !subcategoryIds.includes(id))
      );
    } else {
      // Select all subcategories
      setSelectedGlobalCategories(prev => {
        const newSelection = [...prev];
        subcategoryIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const toggleSubcategory = (subcategoryId: string) => {
    setSelectedGlobalCategories(prev => 
      prev.includes(subcategoryId)
        ? prev.filter(id => id !== subcategoryId)
        : [...prev, subcategoryId]
    );
  };

  const toggleExpandParent = (parentId: string) => {
    setExpandedParentCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(parentId)) {
        newSet.delete(parentId);
      } else {
        newSet.add(parentId);
      }
      return newSet;
    });
  };

  const toggleClassAttributeSection = (classId: string) => {
    setExpandedClassAttributeSections((prev) => {
      const next = new Set(prev);
      if (next.has(classId)) {
        next.delete(classId);
      } else {
        next.add(classId);
      }
      return next;
    });
  };

  const toggleImageAttribute = (name: string) => {
    setImageAttributes((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const toggleGlobalAttribute = (name: string) => {
    setGlobalAttributes((prev) => {
      const next = prev.includes(name)
        ? prev.filter((n) => n !== name)
        : [...prev, name];
      if (!prev.includes(name)) {
        setClasses((classList) =>
          classList.map((cls) => ({
            ...cls,
            attributes: (cls.attributes || []).filter((n) => n !== name),
          }))
        );
      }
      return next;
    });
  };

  const toggleClassAttribute = (classIndex: number, name: string) => {
    const current = classes[classIndex].attributes || [];
    const next = current.includes(name)
      ? current.filter((n) => n !== name)
      : [...current, name];
    updateClass(classIndex, 'attributes', next);
  };

  const renderAttributeCheckbox = (
    attr: LabelAttribute,
    checked: boolean,
    onToggle: () => void,
    disabled = false,
    disabledNote?: string
  ) => (
    <label
      key={attr.name}
      className={`flex items-start space-x-2 py-1 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        disabled={disabled}
        className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <span className="flex-1 min-w-0">
        <span className="text-sm text-gray-900">
          {attr.displayName}{' '}
          <span className="font-mono text-xs text-gray-500">{attr.name}</span>
        </span>
        {attr.description && (
          <span className="block text-xs text-gray-500 mt-0.5">{attr.description}</span>
        )}
        {disabled && disabledNote && (
          <span className="block text-xs text-gray-400 mt-0.5 italic">{disabledNote}</span>
        )}
      </span>
    </label>
  );


  const handleSave = () => {
    if (!name.trim()) {
      alert('Please enter a schema name');
      return;
    }

    if (classes.length === 0 && selectedGlobalCategories.length === 0) {
      alert('Add at least one class or select at least one global category.');
      return;
    }

    const validClasses = classes
      .filter(c => c.name.trim() && c.displayName.trim() && c.subcategory)
      .map((c) => ({
        ...c,
        attributes: c.attributes ?? [],
      }));
    if (classes.length > 0 && validClasses.length === 0) {
      alert('Please provide valid class names and select subcategories for all classes');
      return;
    }

    const schemaData: Partial<LabelSchema> = {
      name: name.trim(),
      scope,
      guidelines: guidelines.trim(),
      classes: validClasses,
      featuredObjects: featuredObjects
        .filter(f => f.objectId)
        .map(f => ({
          objectId: f.objectId,
          ...(f.hotkey && f.hotkey.trim() ? { hotkey: f.hotkey.trim().slice(0, 1).toLowerCase() } : {}),
        })),
      includeGlobalCategories,
      selectedGlobalCategories: includeGlobalCategories ? selectedGlobalCategories : [],
      imageAttributes,
      globalAttributes,
    } as any;

    if (schema) {
      if (createNewVersion) {
        // Creating new version
        schemaData.version = schema.version + 1;
      } else {
        // Editing existing schema - keep same version but update timestamp
        schemaData.version = schema.version;
        schemaData.updatedAt = Date.now();
        schemaData.updatedBy = 'admin'; // TODO: Get from auth context
      }
    } else {
      // Creating new schema
      schemaData.version = 1;
    }

    onSave(schemaData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {schema ? 'Edit Schema' : 'Create Schema'}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Schema Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Tomato Condition"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scope
                  </label>
                  <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                    Detection (Bounding Boxes, Polygons, etc.)
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Schemas are now only used for detection tasks. Classification is handled at the task level.
                  </p>
                </div>
              </div>
            </div>

            {/* Featured objects */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Featured objects</h3>
              <p className="text-sm text-gray-600 mb-2">
                Pin global library objects as shortcuts in the Workbench Detection Objects panel.
                Clicking a featured object writes the same global document id as Object Library.
              </p>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
                Only objects whose subcategory is selected under Global Categories can be featured.
                Select those subcategories first (enable Include Global Categories below if needed).
              </p>

              <div className="space-y-3 mb-4">
                {featuredObjects.length === 0 ? (
                  <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg px-3 py-4 text-center">
                    No featured objects yet
                  </div>
                ) : (
                  featuredObjects.map(featured => {
                    const obj = resolveFeaturedObject(featured.objectId);
                    const displayName = obj?.displayName || obj?.name || featured.objectId;
                    const name = obj?.name || featured.objectId;
                    return (
                      <div
                        key={featured.objectId}
                        className="border border-gray-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{displayName}</div>
                          <div className="text-xs text-gray-500 font-mono truncate">{name}</div>
                          {!obj && (
                            <div className="text-xs text-red-500 mt-1">Global object not found</div>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <span>Hotkey</span>
                            <input
                              type="text"
                              value={featured.hotkey || ''}
                              onChange={(e) => updateFeaturedHotkey(featured.objectId, e.target.value)}
                              maxLength={1}
                              placeholder="—"
                              className="w-10 px-2 py-1 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => removeFeaturedObject(featured.objectId)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <button
                type="button"
                onClick={() => setShowFeaturedSelector(true)}
                className="w-full px-4 py-2 border-2 border-dashed border-blue-300 rounded-lg text-blue-600 hover:border-blue-400 hover:text-blue-800 transition-colors"
              >
                + Add Featured Objects
              </button>
            </div>

            {/* Label Classes */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Label Classes</h3>
              <p className="text-sm text-gray-600 mb-4">
                Define the classes that can be assigned to images
              </p>

              <div className="space-y-4">
                {classes.map((labelClass, index) => (
                  <div key={labelClass.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-gray-900">Class {index + 1}</h4>
                      <button
                        onClick={() => removeClass(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Class ID
                        </label>
                        <input
                          type="text"
                          value={labelClass.name}
                          onChange={(e) => updateClass(index, 'name', e.target.value)}
                          placeholder="e.g., tomato_fresh"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Display Name
                        </label>
                        <input
                          type="text"
                          value={labelClass.displayName}
                          onChange={(e) => updateClass(index, 'displayName', e.target.value)}
                          placeholder="e.g., Fresh Tomato"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description (optional)
                        </label>
                        <input
                          type="text"
                          value={labelClass.description}
                          onChange={(e) => updateClass(index, 'description', e.target.value)}
                          placeholder="Brief description of this class"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hotkey (optional)
                        </label>
                        <input
                          type="text"
                          value={labelClass.hotkey}
                          onChange={(e) => updateClass(index, 'hotkey', e.target.value)}
                          placeholder="e.g., 1, 2, 3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category (optional)
                        </label>
                        <select
                          value={labelClass.category || ''}
                          onChange={(e) => {
                            updateClass(index, 'category', e.target.value);
                            // Clear subcategory if parent category changes and subcategory doesn't belong to new parent
                            if (e.target.value) {
                              const selectedSubcategory = categories.find(cat => cat.id === labelClass.subcategory);
                              if (selectedSubcategory && selectedSubcategory.parentCategoryId !== e.target.value) {
                                updateClass(index, 'subcategory', '');
                              }
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">None (Uncategorized)</option>
                          {parentCategories.map((parent) => (
                            <option key={parent.id} value={parent.id}>
                              {parent.displayName}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Top-level category for grouping</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Subcategory *
                        </label>
                        <select
                          value={labelClass.subcategory || ''}
                          onChange={(e) => updateClass(index, 'subcategory', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select a subcategory...</option>
                          {categories
                            .filter(cat => {
                              // If a parent category is selected, only show subcategories that belong to it
                              if (labelClass.category) {
                                return cat.parentCategoryId === labelClass.category;
                              }
                              // Otherwise show all subcategories
                              return true;
                            })
                            .map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.displayName}
                            </option>
                          ))}
                        </select>
                        {categories.length === 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            No subcategories available. Create subcategories first.
                          </p>
                        )}
                        {labelClass.category && categories.filter(cat => cat.parentCategoryId === labelClass.category).length === 0 && (
                          <p className="text-xs text-yellow-600 mt-1">
                            No subcategories available for the selected category.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Shapes for Detection */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Allowed Shapes
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {['rectangle', 'polygon', 'point', 'line'].map((shape) => (
                          <label key={shape} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={labelClass.shapes?.includes(shape) || false}
                              onChange={(e) => {
                                const currentShapes = labelClass.shapes || [];
                                const newShapes = e.target.checked
                                  ? [...currentShapes, shape]
                                  : currentShapes.filter(s => s !== shape);
                                updateClass(index, 'shapes', newShapes);
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 capitalize">{shape}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Select which shapes can be used to label this class
                      </p>
                    </div>

                    {/* Per-class attributes */}
                    <div className="mt-4 border-t border-gray-100 pt-4">
                      <button
                        type="button"
                        onClick={() => toggleClassAttributeSection(labelClass.id)}
                        className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-gray-900"
                      >
                        <svg
                          className={`w-4 h-4 text-gray-500 transition-transform ${
                            expandedClassAttributeSections.has(labelClass.id) ? 'transform rotate-90' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span>Attributes</span>
                        {(labelClass.attributes?.length ?? 0) > 0 && (
                          <span className="text-xs text-gray-500">
                            ({labelClass.attributes!.length} selected)
                          </span>
                        )}
                      </button>
                      {expandedClassAttributeSections.has(labelClass.id) && (
                        <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                          {attributesLoading ? (
                            <p className="text-xs text-gray-500">Loading attributes...</p>
                          ) : activeInstanceAttributes.length === 0 ? (
                            <p className="text-xs text-gray-500">
                              No instance-level attributes available. Create them in Attributes admin.
                            </p>
                          ) : (
                            activeInstanceAttributes.map((attr) => {
                              const inGlobal = globalAttributes.includes(attr.name);
                              const checked = (labelClass.attributes || []).includes(attr.name);
                              return renderAttributeCheckbox(
                                attr,
                                checked,
                                () => toggleClassAttribute(index, attr.name),
                                inGlobal,
                                inGlobal ? 'already applied to all objects' : undefined
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <div className="flex space-x-2">
                  <button
                    onClick={addClass}
                    className="flex-1 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-colors"
                  >
                    + Add New Class
                  </button>
                  <button
                    onClick={() => setShowObjectSelector(true)}
                    className="flex-1 px-4 py-2 border-2 border-dashed border-blue-300 rounded-lg text-blue-600 hover:border-blue-400 hover:text-blue-800 transition-colors"
                  >
                    + Add Existing Objects
                  </button>
                </div>
              </div>
            </div>

            {/* Global Categories */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Global Categories</h3>
              <p className="text-sm text-gray-600 mb-4">
                Include common objects that can be used across all tasks (e.g., cutting board, knife, plate)
              </p>
              
              <div className="flex items-center space-x-3 mb-4">
                <input 
                  type="checkbox" 
                  id="include-global-categories" 
                  checked={includeGlobalCategories}
                  onChange={(e) => setIncludeGlobalCategories(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                />
                <label htmlFor="include-global-categories" className="text-sm font-medium text-gray-700">
                  Include Global Categories
                </label>
              </div>

              {includeGlobalCategories && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Categories to Include
                  </label>
                  <div className="space-y-1 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {parentCategories.length === 0 ? (
                      <p className="text-xs text-gray-500">No parent categories available. Create parent categories first.</p>
                    ) : (
                      parentCategories.map((parent) => {
                        const subcategories = getSubcategoriesForParent(parent.id);
                        const isExpanded = expandedParentCategories.has(parent.id);
                        const allSelected = areAllSubcategoriesSelected(parent.id);
                        const someSelected = areSomeSubcategoriesSelected(parent.id);
                        const selectedCount = subcategories.filter(sub => selectedGlobalCategories.includes(sub.id)).length;

                        return (
                          <div key={parent.id} className="border-b border-gray-100 last:border-b-0 pb-2 last:pb-0">
                            {/* Parent Category Row */}
                            <div className="flex items-center space-x-2 py-1">
                        <input
                          type="checkbox"
                                checked={allSelected}
                                ref={(input) => {
                                  if (input) {
                                    input.indeterminate = someSelected;
                                  }
                                }}
                                onChange={() => toggleParentCategory(parent.id)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <button
                                type="button"
                                onClick={() => toggleExpandParent(parent.id)}
                                className="flex items-center space-x-1 flex-1 text-left hover:bg-gray-50 px-1 py-0.5 rounded"
                              >
                                <svg
                                  className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="text-sm font-medium text-gray-700 flex-1">
                                  {parent.displayName}
                                </span>
                                {subcategories.length > 0 && (
                                  <span className="text-xs text-gray-500">
                                    ({selectedCount}/{subcategories.length} selected)
                                  </span>
                                )}
                              </button>
                            </div>

                            {/* Subcategories (when expanded) */}
                            {isExpanded && subcategories.length > 0 && (
                              <div className="ml-6 mt-1 space-y-1">
                                {subcategories.map((subcategory) => (
                                  <label key={`${parent.id}-${subcategory.id}`} className="flex items-center space-x-2 py-0.5">
                                    <input
                                      type="checkbox"
                                      checked={selectedGlobalCategories.includes(subcategory.id)}
                                      onChange={() => toggleSubcategory(subcategory.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                                    <span className="text-sm text-gray-600">{subcategory.displayName}</span>
                      </label>
                    ))}
                              </div>
                            )}
                            {isExpanded && subcategories.length === 0 && (
                              <div className="ml-6 mt-1">
                                <p className="text-xs text-gray-400 italic">No subcategories</p>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                    {/* Uncategorized subcategories (no parent) */}
                    {(() => {
                      const uncategorizedSubs = categories.filter(cat => !cat.parentCategoryId);
                      // Deduplicate by ID to prevent duplicate keys
                      const seen = new Set<string>();
                      const uniqueUncategorized = uncategorizedSubs.filter(cat => {
                        if (seen.has(cat.id)) {
                          return false;
                        }
                        seen.add(cat.id);
                        return true;
                      });
                      if (uniqueUncategorized.length === 0) return null;
                      
                      return (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <div className="text-xs font-medium text-gray-500 mb-2">Uncategorized Subcategories</div>
                          <div className="space-y-1">
                            {uniqueUncategorized.map((subcategory) => (
                              <label key={`uncategorized-${subcategory.id}`} className="flex items-center space-x-2 py-0.5">
                                <input
                                  type="checkbox"
                                  checked={selectedGlobalCategories.includes(subcategory.id)}
                                  onChange={() => toggleSubcategory(subcategory.id)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-600">{subcategory.displayName}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Only objects from selected subcategories will be available in the labeling workspace. Select a parent category to include all its subcategories, or expand to select individual subcategories.
                  </p>
                </div>
              )}
            </div>

            {/* Schema-level attributes */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Schema Attributes</h3>
              <p className="text-sm text-gray-600 mb-4">
                Reference label attributes by name. Definitions live in the Attributes catalog.
              </p>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image-level attributes
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {attributesLoading ? (
                      <p className="text-xs text-gray-500">Loading attributes...</p>
                    ) : activeImageAttributes.length === 0 ? (
                      <p className="text-xs text-gray-500">No image-level attributes available.</p>
                    ) : (
                      activeImageAttributes.map((attr) =>
                        renderAttributeCheckbox(
                          attr,
                          imageAttributes.includes(attr.name),
                          () => toggleImageAttribute(attr.name)
                        )
                      )
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Attributes on every object
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    These apply to every bounding box drawn in this schema.
                  </p>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {attributesLoading ? (
                      <p className="text-xs text-gray-500">Loading attributes...</p>
                    ) : activeInstanceAttributes.length === 0 ? (
                      <p className="text-xs text-gray-500">No instance-level attributes available.</p>
                    ) : (
                      activeInstanceAttributes.map((attr) =>
                        renderAttributeCheckbox(
                          attr,
                          globalAttributes.includes(attr.name),
                          () => toggleGlobalAttribute(attr.name)
                        )
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Version Control (only show when editing existing schema) */}
            {schema && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Version Control</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input 
                      type="radio" 
                      id="edit-existing" 
                      name="version-control"
                      checked={!createNewVersion}
                      onChange={() => setCreateNewVersion(false)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" 
                    />
                    <label htmlFor="edit-existing" className="text-sm font-medium text-gray-700">
                      Edit existing schema (v{schema.version})
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input 
                      type="radio" 
                      id="create-new-version" 
                      name="version-control"
                      checked={createNewVersion}
                      onChange={() => setCreateNewVersion(true)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" 
                    />
                    <label htmlFor="create-new-version" className="text-sm font-medium text-gray-700">
                      Create new version (v{schema.version + 1})
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 ml-7">
                    {!createNewVersion 
                      ? "This will update the existing schema in place. All tasks using this schema will see the changes immediately."
                      : "This will create a new version of the schema. Existing tasks will continue using the current version."
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Guidelines */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Guidelines (optional)</h3>
              <p className="text-sm text-gray-600 mb-4">
                Provide instructions for human annotators
              </p>
              <textarea
                value={guidelines}
                onChange={(e) => setGuidelines(e.target.value)}
                placeholder="Enter guidelines for labeling..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {schema 
                ? (createNewVersion ? 'Create New Version' : 'Save Changes')
                : 'Create Schema'
              }
            </button>
          </div>
        </div>
      </div>

      {/* Object Selector Modal */}
      {showObjectSelector && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Existing Objects</h3>
              
              <div className="mb-4">
                <input
                  type="text"
                  value={objectSearchTerm}
                  onChange={(e) => setObjectSearchTerm(e.target.value)}
                  placeholder="Search objects..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredObjects.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No objects found
                  </div>
                ) : (
                  filteredObjects.map((obj) => (
                    <div
                      key={obj.id || obj.name}
                      className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                        selectedObjects.includes(obj.id) ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => toggleObjectSelection(obj.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">{obj.displayName || obj.name}</div>
                          {obj.description && (
                            <div className="text-xs text-gray-400 mt-1">{obj.description}</div>
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={selectedObjects.includes(obj.id)}
                          onChange={() => toggleObjectSelection(obj.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowObjectSelector(false);
                    setSelectedObjects([]);
                    setObjectSearchTerm('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={addSelectedObjects}
                  disabled={selectedObjects.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Add Selected ({selectedObjects.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Featured Objects Selector Modal */}
      {showFeaturedSelector && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Add Featured Objects</h3>
              <p className="text-xs text-gray-500 mb-4">
                Only globals in selected Global Categories subcategories are listed. Selection stores the global document id.
              </p>

              <div className="mb-4">
                <input
                  type="text"
                  value={featuredSearchTerm}
                  onChange={(e) => setFeaturedSearchTerm(e.target.value)}
                  placeholder="Search objects..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredFeatureableGlobals.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {selectedGlobalCategories.length === 0
                      ? 'Select Global Categories subcategories first'
                      : 'No objects found'}
                  </div>
                ) : (
                  filteredFeatureableGlobals.map((obj: any) => (
                    <div
                      key={obj.id}
                      className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                        selectedFeaturedIds.includes(obj.id) ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => toggleFeaturedSelection(obj.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900">{obj.displayName || obj.name}</div>
                          <div className="text-xs text-gray-500 font-mono truncate">{obj.name || obj.id}</div>
                        </div>
                        <input
                          type="checkbox"
                          checked={selectedFeaturedIds.includes(obj.id)}
                          onChange={() => toggleFeaturedSelection(obj.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowFeaturedSelector(false);
                    setSelectedFeaturedIds([]);
                    setFeaturedSearchTerm('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={addSelectedFeaturedObjects}
                  disabled={selectedFeaturedIds.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  Add Selected ({selectedFeaturedIds.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
